import { useCallback, useEffect, useState } from "react";

import { useDialogs } from "@/components/shell/Dialogs";
import { TopBar } from "@/components/shell/TopBar";
import { listSetupDecks } from "@/lib/setup";
import {
  cancelJob,
  jobRunErrors,
  jobRuns,
  listJobs,
  retryJob,
  runJob,
  saveJobSettings,
  saveTrigger,
  subscribeToJobs,
  type Job,
  type JobError,
  type JobProgress,
  type JobRun,
  type Trigger,
} from "@/lib/jobs";

import { DeckOptionsForm } from "./DeckOptions";
import { DecksPanel } from "./DecksPanel";
import { JobDetail } from "./JobDetail";
import { JobsCardMobile } from "./JobsCardMobile";
import { JobsPanel } from "./JobsPanel";
import { LogsPanel } from "./LogsPanel";
import { ModelsPanel } from "./ModelsPanel";
import { ReviewPanel } from "./ReviewPanel";
import { RunHistory } from "./RunHistory";
import { TagsPanel } from "./TagsPanel";

/**
 * Setup — `5e`. Four stacked panels: Decks, Jobs, Tags, Models.
 *
 * Unsplit, and capped at `--content-max` and centred past it: nothing here rearranges between
 * widths, there is simply more room. On a phone (`6j`) the same cards stack, and tapping a
 * job opens its detail (`6j2`–`6j4`) in place of the list.
 */
export function Setup({
  isPhone = false,
  onOpenCard,
  openJobId,
  onOpenJob,
  historyOpen,
  onHistoryOpenChange,
  openDeckId = null,
  onOpenDeck,
}: {
  isPhone?: boolean;
  onOpenCard?: (noteId: number) => void;
  /**
   * The two drill-ins are navigation, so they are held by `App` and carried in the URL rather than
   * in local state — that is what makes Back leave a job's detail and a refresh return to it.
   * `JobDetail`'s own `‹` and this surface's Back do the same thing for the same reason.
   */
  openJobId: string | null;
  onOpenJob: (jobId: string | null) => void;
  historyOpen: boolean;
  onHistoryOpenChange: (open: boolean) => void;
  /** The phone's deck-options page (日本-344) — navigation, so held by `App` like `openJobId`. */
  openDeckId?: number | null;
  onOpenDeck?: (deckId: number | null) => void;
}) {
  const [error, setError] = useState<string | null>(null);

  const report = useCallback((e: unknown) => {
    setError(e instanceof Error ? e.message : String(e));
  }, []);

  const jobs = useJobs(report);

  // Clear a stale error when a later action succeeds, so the banner never outlives its cause.
  const clear = useCallback(() => setError(null), []);

  useEffect(() => {
    if (!error) return;
    const handle = window.setTimeout(() => setError(null), 8000);
    return () => window.clearTimeout(handle);
  }, [error]);

  const openCard = useCallback(
    (noteId: number) => onOpenCard?.(noteId),
    [onOpenCard],
  );

  const openJob = openJobId ? (jobs.jobs.find((j) => j.id === openJobId) ?? null) : null;
  if (isPhone && openJob) {
    return (
      <JobDetail
        job={openJob}
        jobs={jobs.jobs}
        live={jobs.live?.jobId === openJob.id ? jobs.live : null}
        runs={jobs.runs[openJob.id] ?? []}
        errors={openJob.lastRun ? (jobs.errors[openJob.lastRun.id] ?? []) : []}
        onBack={() => onOpenJob(null)}
        onRun={() => jobs.run(openJob.id)}
        onCancel={() => jobs.cancel(openJob.id)}
        onRetry={() => jobs.retry(openJob.id)}
        onSaveTrigger={(trigger) => jobs.save(openJob.id, trigger)}
        onSaveSettings={(values) => jobs.saveSettings(openJob.id, values)}
        onError={report}
        onOpenCard={openCard}
      />
    );
  }

  if (isPhone && openDeckId !== null) {
    return (
      <DeckOptionsPage
        deckId={openDeckId}
        onBack={() => onOpenDeck?.(null)}
        onError={report}
      />
    );
  }

  const failed = jobs.jobs.filter((j) => j.state === "failed" && j.trigger.notifyOnFailure);

  return (
    <>
      {/* No subtitle on the phone: the desktop's is a list of the panels below it, which is a
          useful summary of a screen you see all of and is 26 truncated characters of one you
          scroll. The 設定 frame draws the word alone. */}
      <TopBar
        title="設定"
        subtitle={isPhone ? undefined : "デッキ · 復習 · ジョブ · タグ · モデル"}
        destinationId="setup"
      />
      <div className="min-h-0 flex-1 overflow-auto">
        <div
          className={`mx-auto flex max-w-[var(--content-max)] flex-col ${
            isPhone ? "gap-[11px] p-[13px]" : "gap-[18px] p-[22px]"
          }`}
        >
          {error && (
            <p className="rounded-[var(--r-3)] border border-accent-line bg-accent-ground px-4 py-3 text-secondary text-accent-hi">
              {error}
            </p>
          )}
          {/* `notifyOnFailure` has no external channel: it means this banner, and the red count on
              the Setup rail item. A failed maintenance run that says nothing is the failure mode
              the whole panel exists to prevent.

              **On a phone it carries a button, and that is behaviour rather than decoration.** The
              badge on the tab bar is what brought you here, and the desktop's "the details are in
              the job below" is a five-panel scroll away at 390px — so the banner opens the job
              detail directly. With more than one failure it names the first, because the detail is
              a single job's screen and picking one silently would be worse than saying which. */}
          {failed.length > 0 && (
            <div className="flex items-center gap-[10px] rounded-[var(--r-3)] border-[1.5px] border-accent-line bg-accent-ground px-4 py-3">
              <span
                aria-hidden
                className="size-2 shrink-0 rounded-full bg-[color:var(--accent)]"
              />
              <p className="min-w-0 flex-1 text-secondary text-ink-2">
                {failed.map((j) => j.name).join("、")}が前回失敗しました。
                {!isPhone && "詳細は下のジョブに。"}
              </p>
              {isPhone && (
                <PanelButton tone="danger" onClick={() => onOpenJob(failed[0]!.id)}>
                  確認
                </PanelButton>
              )}
            </div>
          )}
          <DecksPanel
            onError={report}
            onSuccess={clear}
            onOpenDeckOptions={isPhone ? (deckId) => onOpenDeck?.(deckId) : undefined}
          />
          {/* Beside Decks, because the two are the study pair: which cards come up, and how long a
              sitting may sit before it is pushed and closed. */}
          <ReviewPanel onError={report} onSuccess={clear} />
          {isPhone ? (
            <JobsCardMobile
              jobs={jobs.jobs}
              live={jobs.live}
              onOpen={onOpenJob}
              onRun={jobs.run}
              onRetry={jobs.retry}
            />
          ) : historyOpen ? (
            <RunHistory jobs={jobs.jobs} onClose={() => onHistoryOpenChange(false)} />
          ) : (
            <JobsPanel
              jobs={jobs.jobs}
              live={jobs.live}
              errors={jobs.errors}
              onRun={jobs.run}
              onCancel={jobs.cancel}
              onRetry={jobs.retry}
              onSaveTrigger={jobs.save}
              onSaveSettings={jobs.saveSettings}
              onError={report}
              onOpenHistory={() => onHistoryOpenChange(true)}
              onOpenCard={openCard}
            />
          )}
          <TagsPanel onError={report} onSuccess={clear} />
          {/* Last, and deliberately below Tags: it is the panel you visit twice a year, not the
              one you visit after a run. */}
          <ModelsPanel onError={report} onSuccess={clear} />
          {/* Below Models, because it is the panel you open when Models has not done what you
              expected — the record, not the setting. */}
          <LogsPanel onError={report} />
        </div>
      </div>
    </>
  );
}

/**
 * The Jobs surface's data: the list, the live frame, run history and per-run errors.
 *
 * Subscribed for the panel's whole life rather than only during a run — a job started from a phone,
 * or by the scheduler at 03:00, has to show up here too.
 */
function useJobs(onError: (error: unknown) => void) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [live, setLive] = useState<JobProgress | null>(null);
  const [runs, setRuns] = useState<Record<string, JobRun[]>>({});
  const [errors, setErrors] = useState<Record<number, JobError[]>>({});

  const load = useCallback(() => {
    void listJobs()
      .then((snapshot) => {
        setJobs(snapshot.jobs);
        // Adopt the in-flight frame: SSE only carries what happens after you subscribe, so a page
        // opened mid-run would otherwise show a running job with no numbers until the next card.
        setLive(snapshot.live);
      })
      .catch(onError);
  }, [onError]);

  useEffect(load, [load]);

  useEffect(() => {
    return subscribeToJobs({
      onProgress: setLive,
      onStatus: (status) => {
        // Cleared only when the frame is about the job the live card belongs to. Since 日本-307 a
        // status can name a job that is not the running one — cancelling a QUEUED row publishes
        // `idle` for it — and blanking the running job's counters on that flickers the progress
        // bar until `load()`'s snapshot puts them back.
        setLive((current) =>
          current && (status.state === "running" || current.jobId !== status.jobId)
            ? current
            : null,
        );
        load();
      },
    });
  }, [load]);

  // Run history and error rows follow the list rather than being fetched per click: the failed row
  // is expanded by default, so its errors have to be there when it draws.
  useEffect(() => {
    let cancelled = false;
    for (const job of jobs) {
      void jobRuns(job.id)
        .then(({ runs: rows }) => {
          if (!cancelled) setRuns((current) => ({ ...current, [job.id]: rows }));
        })
        .catch(() => undefined);
      const last = job.lastRun;
      if (last && last.errorCount > 0) {
        void jobRunErrors(job.id, last.id)
          .then(({ errors: rows }) => {
            if (!cancelled) setErrors((current) => ({ ...current, [last.id]: rows }));
          })
          .catch(() => undefined);
      }
    }
    return () => {
      cancelled = true;
    };
    // Keyed on the verdicts rather than the array: the list is replaced on every SSE frame, and
    // refetching every job's history each time would be a request per progress tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs.map((j) => `${j.id}:${j.state}:${j.lastRun?.id ?? 0}`).join("|")]);

  const act = useCallback(
    (action: Promise<unknown>) => {
      void action.catch(onError).finally(load);
    },
    [load, onError],
  );

  return {
    jobs,
    live,
    runs,
    errors,
    run: useCallback((id: string) => act(runJob(id)), [act]),
    cancel: useCallback((id: string) => act(cancelJob(id)), [act]),
    retry: useCallback((id: string) => act(retryJob(id)), [act]),
    // Returns the promise rather than swallowing it: the editor snaps its draft back to the
    // stored schedule when a save is refused, which it can only do if it learns that it was.
    save: useCallback(
      (id: string, trigger: Trigger) =>
        saveTrigger(id, trigger).then((updated) => {
          setJobs((current) => current.map((j) => (j.id === updated.id ? updated : j)));
          return updated;
        }),
      [],
    ),
    // Same shape and the same reason: the settings editor snaps its field back when a value is
    // refused, which it can only do if the rejection reaches it.
    saveSettings: useCallback(
      (id: string, values: Record<string, number>) =>
        saveJobSettings(id, values).then((updated) => {
          setJobs((current) => current.map((j) => (j.id === updated.id ? updated : j)));
          return updated;
        }),
      [],
    ),
  };
}

/** The shared frame every Setup panel sits in. */
export function Panel({
  title,
  subtitle,
  actions,
  children,
}: {
  title: string;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-[var(--r-4)] border border-line bg-panel shadow-[var(--sh-frame)]">
      <header className="flex flex-wrap items-center gap-[10px] border-b border-line-soft px-[18px] py-3">
        <h2 className="text-title font-semibold text-ink">{title}</h2>
        {subtitle && <span className="text-secondary text-ink-3">{subtitle}</span>}
        <span className="flex-1" />
        {actions}
      </header>
      {children}
    </section>
  );
}

export function PanelButton({
  onClick,
  tone = "normal",
  disabled,
  children,
  check,
}: {
  onClick: () => void;
  tone?: "normal" | "primary" | "danger";
  disabled?: boolean;
  children: React.ReactNode;
  /** `checks/` hook. Optional — only the controls a browser check drives need one. */
  check?: string;
}) {
  const style =
    tone === "primary"
      ? "bg-ink text-void border-transparent"
      : tone === "danger"
        ? "border-accent-line bg-accent-tint text-accent-hi"
        : "border-line-strong bg-void text-ink-2";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-check={check}
      className={`shrink-0 rounded-[var(--r-2)] border px-[11px] py-[6px] text-[14px]/[1] font-semibold disabled:opacity-40 ${style}`}
    >
      {children}
    </button>
  );
}

/**
 * The phone's deck-options page (日本-344, frame D) — JobDetail's shape: a 50px header with `‹`,
 * the form, and a save bar pinned to the bottom.
 *
 * **Only the `‹` asks before discarding unsaved changes.** Browser Back is a `popstate`, which
 * cannot be held open for a dialog, so it leaves without asking; that was a known limit of the plan.
 */
function DeckOptionsPage({
  deckId,
  onBack,
  onError,
}: {
  deckId: number;
  onBack: () => void;
  onError: (error: unknown) => void;
}) {
  const dialogs = useDialogs();
  const [changed, setChanged] = useState(0);
  const [name, setName] = useState<string | null>(null);

  useEffect(() => {
    void listSetupDecks()
      .then(({ decks }) => {
        const deck = decks.find((d) => d.id === deckId);
        setName(deck ? (deck.name.split("::").pop() ?? deck.name) : null);
      })
      .catch(onError);
  }, [deckId, onError]);

  const back = async () => {
    if (changed > 0) {
      const ok = await dialogs.confirm({
        title: "変更を破棄しますか？",
        body: `${name ?? ""}の変更 ${changed}件は保存されていません。`,
        confirmLabel: "破棄",
        tone: "danger",
      });
      if (!ok) return;
    }
    onBack();
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex h-[50px] shrink-0 items-center gap-2.5 border-b border-line bg-panel px-3.5">
        <button
          type="button"
          onClick={() => void back()}
          aria-label="設定へ戻る"
          data-check="deck-options.back"
          className="grid size-[44px] -ml-3 place-items-center text-[18px] text-ink-2"
        >
          ‹
        </button>
        <span className="flex-1 text-[16px]/[1] font-semibold text-ink">{name ?? ""}</span>
      </header>
      <DeckOptionsForm deckId={deckId} layout="page" onDirtyChange={setChanged} onSaved={() => {}} />
    </div>
  );
}
